import React, { useState } from 'react';
import { Table, Button, Badge, Collapse, Card, OverlayTrigger, Tooltip, SortableTh, RowActions, ACTIONS_COL_WIDTH } from '@/components/ui';
import type { Role } from '../../../types/Role';
import type { Permission } from '../../../types/User';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { displayModuleForPermission } from '../utils/permissionModules';

interface RolesTableProps {
  roles: Role[];
  permissions: Permission[];
  onEditRole: (role: Role) => void;
  onDeleteRole: (roleId: number) => void;
  sortBy?: string | null;
  sortDir?: 'asc' | 'desc';
  onSort?: (key: string, dir: 'asc' | 'desc') => void;
}

// Component for permission badge with controlled tooltip
const PermissionBadgeWithTooltip: React.FC<{ permission: Permission }> = ({ permission }) => {
  const [show, setShow] = useState(false);

  if (!permission.description && !permission.usage) {
    return (
      <Badge
        bg="light"
        text="dark"
        className="tw-mr-1 tw-mb-1"
      >
        {permission.name}
      </Badge>
    );
  }

  return (
    <OverlayTrigger
      placement="top"
      show={show}
      overlay={
        <Tooltip 
          id={`permission-tooltip-${permission.id}`} 
          style={{ maxWidth: '450px' }}
          onMouseEnter={() => setShow(true)}
          onMouseLeave={() => setShow(false)}
        >
          <PermissionTooltipContent permission={permission} />
        </Tooltip>
      }
      trigger={[]}
    >
      <Badge
        bg="light"
        text="dark"
        className="tw-mr-1 tw-mb-1"
        style={{ cursor: 'help' }}
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
      >
        {permission.name}
      </Badge>
    </OverlayTrigger>
  );
};

// Expandable tooltip content component for permissions
const PermissionTooltipContent: React.FC<{ permission: Permission }> = ({ permission }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (!permission.description && !permission.usage) {
    return <div>No description available</div>;
  }

  return (
    <div style={{ textAlign: 'left' }}>
      {permission.description && (
        <div className="tw-mb-2">
          <strong>Description:</strong>
          <div style={{ fontSize: '0.9rem', marginTop: '4px' }}>{permission.description}</div>
        </div>
      )}
      {permission.usage && (
        <>
          {!isExpanded ? (
            <div>
              <Button
                variant="link"
                size="sm"
                className="tw-p-0 tw-text-white tw-underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(true);
                }}
                style={{ fontSize: '0.85rem' }}
              >
                Show usage details...
              </Button>
            </div>
          ) : (
            <div>
              <div className="tw-mb-2">
                <strong>Usage:</strong>
                <div style={{ fontSize: '0.9rem', marginTop: '4px', whiteSpace: 'pre-wrap' }}>
                  {permission.usage}
                </div>
              </div>
              <Button
                variant="link"
                size="sm"
                className="tw-p-0 tw-text-white tw-underline"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsExpanded(false);
                }}
                style={{ fontSize: '0.85rem' }}
              >
                Show less
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const RolesTable: React.FC<RolesTableProps> = ({
  roles,
  permissions,
  onEditRole,
  onDeleteRole,
  sortBy = null,
  sortDir = 'asc',
  onSort
}) => {
  const [expandedUserRoles, setExpandedUserRoles] = useState<Set<number>>(new Set());
  const [expandedPermissionRoles, setExpandedPermissionRoles] = useState<Set<number>>(new Set());
  const { hasPermission } = usePermissions();
  const { currentUser, loading: authLoading } = useAuth();
  const isAdministrator = !authLoading && currentUser?.role?.name === 'Admin';

  const toggleUsersExpansion = (roleId: number) => {
    const newExpandedUsers = new Set(expandedUserRoles);
    if (newExpandedUsers.has(roleId)) {
      newExpandedUsers.delete(roleId);
    } else {
      newExpandedUsers.add(roleId);
    }
    setExpandedUserRoles(newExpandedUsers);

    // Keep Users/Permissions details independent and never open together for the same role
    const newExpandedPermissions = new Set(expandedPermissionRoles);
    newExpandedPermissions.delete(roleId);
    setExpandedPermissionRoles(newExpandedPermissions);
  };

  const togglePermissionsExpansion = (roleId: number) => {
    const newExpandedPermissions = new Set(expandedPermissionRoles);
    if (newExpandedPermissions.has(roleId)) {
      newExpandedPermissions.delete(roleId);
    } else {
      newExpandedPermissions.add(roleId);
    }
    setExpandedPermissionRoles(newExpandedPermissions);

    // Keep Users/Permissions details independent and never open together for the same role
    const newExpandedUsers = new Set(expandedUserRoles);
    newExpandedUsers.delete(roleId);
    setExpandedUserRoles(newExpandedUsers);
  };

  const getRolePermissions = (role: Role) => {
    const accessKeys = role.access?.length
      ? role.access
      : (role as { RolePermission?: { permission?: { key: string } }[] }).RolePermission
          ?.map(rp => rp.permission?.key)
          .filter((k): k is string => !!k) ?? [];
    const permissionsByKey = new Map(permissions.map((p) => [p.key, p]));
    const uniqueAccessKeys = Array.from(new Set(accessKeys));

    return uniqueAccessKeys.map((key, idx) => {
      const known = permissionsByKey.get(key);
      if (known) return known;

      // Fallback for unexpected/missing permission rows: keep the count accurate.
      return {
        id: -1000 - idx,
        key,
        name: key,
        module: 'Unknown',
      } as Permission;
    });
  };

  const getTrueContextPermissions = (role: Role): Permission[] => {
    const trueContext: Array<{
      key: string;
      name: string;
      module: string;
      enabled: boolean | undefined;
    }> = [
      {
        key: 'truecontext:road_inspector',
        name: 'Road Inspector',
        module: 'TrueContext Configuration',
        enabled: role.isRoadInspector,
      },
      {
        key: 'truecontext:bridge_inspector',
        name: 'Bridge Inspector',
        module: 'TrueContext Configuration',
        enabled: role.isBridgeInspector,
      },
      {
        key: 'truecontext:supervisor',
        name: 'Supervisor',
        module: 'TrueContext Configuration',
        enabled: role.isSupervisor,
      },
      {
        key: 'truecontext:purchase_requisition_access',
        name: 'Purchase Requisition Access',
        module: 'TrueContext Configuration',
        enabled: role.hasPurchaseRequisitionAccess,
      },
      {
        key: 'truecontext:mechanic_daily_log_access',
        name: 'Mechanic Daily Log Access',
        module: 'TrueContext Configuration',
        enabled: role.hasMechanicDailyLogAccess,
      },
    ];

    // Synthetic permissions so TrueContext settings participate in the same count/listing UI
    return trueContext
      .filter((p) => p.enabled)
      .map((p, idx) => ({
        id: -1 * (idx + 1),
        key: p.key,
        name: p.name,
        module: p.module,
      }));
  };

  const groupPermissionsByModule = (rolePermissions: Permission[]) => {
    return rolePermissions.reduce((acc, permission) => {
      const module = displayModuleForPermission(permission.key, permission.module);
      if (!acc[module]) {
        acc[module] = [];
      }
      acc[module].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
  };

  return (
    <div>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th style={{ width: '5%' }}>#</th>
            <SortableTh label="Role Name" sortKey="name" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ width: '25%' }} />
            <SortableTh label="Users Count" sortKey="usersCount" activeKey={sortBy} direction={sortDir} onSort={onSort} style={{ width: '20%' }} />
            <th style={{ width: '25%' }}>Permissions</th>
            <th style={{ width: ACTIONS_COL_WIDTH }} className="tw-text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {roles.map((role, index) => {
            const rolePermissions = getRolePermissions(role);
            const trueContextPermissions = getTrueContextPermissions(role);
            const allRolePermissions = [...rolePermissions, ...trueContextPermissions];
            const uniqueByKey = new Map<string, Permission>();
            for (const p of allRolePermissions) {
              if (!uniqueByKey.has(p.key)) uniqueByKey.set(p.key, p);
            }
            const allRolePermissionsUnique = Array.from(uniqueByKey.values());
            const isUsersExpanded = expandedUserRoles.has(role.id);
            const isPermissionsExpanded = expandedPermissionRoles.has(role.id);
            const hasAssignedUsers = (role.users?.length || 0) > 0;

            return (
              <React.Fragment key={role.id}>
                <tr>
                  <td>{index + 1}</td>
                  <td>
                    <strong>{role.name}</strong>
                  </td>
                  <td>
                    <div className="tw-flex tw-items-center">
                      <Badge bg="info">{role.users?.length || 0} users</Badge>
                      {isAdministrator && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="tw-ml-2"
                          onClick={() => toggleUsersExpansion(role.id)}
                          aria-label={`${isUsersExpanded ? 'Hide' : 'Show'} Users Details`}
                        >
                          {isUsersExpanded ? 'Hide' : 'Show'} Details
                          <i className={`bi bi-chevron-${isUsersExpanded ? 'up' : 'down'} tw-ml-1`}></i>
                        </Button>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="tw-flex tw-items-center">
                      <Badge bg="secondary" className="tw-mr-2">
                        {allRolePermissionsUnique.length} permissions
                      </Badge>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => togglePermissionsExpansion(role.id)}
                        aria-label={`${isPermissionsExpanded ? 'Hide' : 'Show'} Permissions Details`}
                      >
                        {isPermissionsExpanded ? 'Hide' : 'Show'} Details
                        <i className={`bi bi-chevron-${isPermissionsExpanded ? 'up' : 'down'} tw-ml-1`}></i>
                      </Button>
                    </div>
                  </td>
                  <td>
                    <RowActions
                      actions={[
                        {
                          kind: 'edit',
                          label: 'Edit Role',
                          onClick: () => onEditRole(role),
                          hidden: !hasPermission('role:edit'),
                        },
                        {
                          kind: 'delete',
                          // The disabled reason has to travel in the label — a kebab item has no
                          // hover target of its own to hang a tooltip on.
                          label: hasAssignedUsers
                            ? 'Delete Role — role has assigned users'
                            : 'Delete Role',
                          onClick: () => onDeleteRole(role.id),
                          disabled: hasAssignedUsers,
                          hidden: !hasPermission('role:delete'),
                        },
                      ]}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                    <Collapse in={isAdministrator && isUsersExpanded}>
                      <div>
                        <Card className="tw-m-2">
                          <Card.Body>
                            <h6>Users for {role.name}</h6>
                            {role.users?.length ? (
                              <Table striped bordered hover responsive size="sm">
                                <thead>
                                  <tr>
                                    <th style={{ width: '5%' }}>#</th>
                                    <th style={{ width: '45%' }}>Name</th>
                                    <th style={{ width: '50%' }}>Email</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(role.users || []).map((user, userIndex) => (
                                    <tr key={user.id}>
                                      <td>{userIndex + 1}</td>
                                      <td>
                                        <strong>
                                          {user.name} {user.lastName}
                                        </strong>
                                      </td>
                                      <td>
                                        {user.email}{' '}
                                        {user.status && (
                                          <Badge
                                            bg={user.status === 'ACTIVE' ? 'success' : 'secondary'}
                                            className="tw-ml-2"
                                          >
                                            {user.status}
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            ) : (
                              <p className="tw-text-content-muted tw-mb-0">No users assigned to this role.</p>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </Collapse>
                  </td>
                </tr>
                <tr>
                  <td colSpan={5} style={{ padding: 0, border: 'none' }}>
                    <Collapse in={isPermissionsExpanded}>
                      <div>
                        <Card className="tw-m-2">
                          <Card.Body>

                            <h6>Permissions for {role.name}</h6>
                            {allRolePermissionsUnique.length === 0 ? (
                              <p className="tw-text-content-muted">No permissions assigned</p>
                            ) : (
                              <div>
                                {Object.entries(groupPermissionsByModule(allRolePermissionsUnique)).map(([module, perms]) => (
                                  <div key={module} className="tw-mb-3">
                                    <h6 className="tw-text-primary">{module}</h6>
                                    <div className="tw-flex tw-flex-wrap tw-gap-1">
                                      {perms.map(permission => (
                                        <PermissionBadgeWithTooltip 
                                          key={permission.id} 
                                          permission={permission} 
                                        />
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </Card.Body>
                        </Card>
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </Table>
    </div>
  );
};

export default RolesTable;